import { useState } from "react";

export const landmarks = [
  { name: "Jacksonville International Airport (JAX)", icon: "ri-flight-takeoff-line", distance: "~15 miles from downtown — Northside", desc: "Primary commercial airport for Northeast Florida and Southeast Georgia with nonstop service to major domestic hubs and international destinations including Toronto. Also supports military travel for NAS Jacksonville and Mayport personnel.", url: "https://www.flyjacksonville.com/", schemaType: "Airport" },
  { name: "JAXPORT Cruise Terminal", icon: "ri-ship-line", distance: "~15 miles north — near JAX Airport", desc: "Cruise terminal serving Carnival Cruise Line with year-round Bahamas and Caribbean itineraries from Jacksonville. All cruise passengers require valid passports for international cruise travel.", url: "https://www.jaxport.com/", schemaType: "TouristAttraction" },
  { name: "Jacksonville Beach", icon: "ri-landscape-line", distance: "~20 miles east — Atlantic coast", desc: "Popular beach destination with fishing pier, golf course, and vibrant boardwalk area. One of Jacksonville's primary coastal communities drawing visitors from across Northeast Florida and Southeast Georgia.", url: "https://www.jacksonvillebeach.org/", schemaType: "TouristAttraction" },
  { name: "TIAA Bank Field", icon: "ri-building-line", distance: "Downtown Jacksonville — St. Johns River", desc: "Home of the Jacksonville Jaguars NFL team and host of the annual Florida-Georgia college football rivalry game. Also hosts major concerts and events — a defining sports landmark in downtown Jacksonville.", url: "https://www.tiaabankfield.com/", schemaType: "TouristAttraction" },
  { name: "St. Johns Town Center", icon: "ri-store-2-line", distance: "~13 miles southeast — Southside", desc: "Major open-air shopping destination with 150+ stores including Nordstrom, Apple, Louis Vuitton, and Tiffany. Largest retail center in Northeast Florida drawing shoppers from across the region.", url: "https://www.simon.com/mall/st-johns-town-center", schemaType: "TouristAttraction" },
  { name: "NAS Jacksonville", icon: "ri-building-2-line", distance: "~10 miles south — Westside along St. Johns River", desc: "Major U.S. Navy installation and master air station. Home to P-8 Poseidon maritime patrol squadrons and helicopter units with thousands of active-duty personnel. Military travel drives significant passport demand.", url: "https://www.cnic.navy.mil/jacksonville/", schemaType: "TouristAttraction" },
  { name: "Mayport Naval Station", icon: "ri-anchor-line", distance: "~20 miles east — Atlantic coast near Jacksonville Beach", desc: "U.S. Navy base at the mouth of the St. Johns River, home to surface warships including destroyers and cruisers. A major military installation with frequent deployment-related passport needs.", url: "https://www.cnic.navy.mil/jacksonville/", schemaType: "TouristAttraction" },
  { name: "Daytona Beach Intl Airport (DAB)", icon: "ri-flight-takeoff-line", distance: "~90 miles south — Daytona Beach", desc: "Secondary airport option for Northeast Florida travelers, especially for budget carriers and seasonal routes. Popular alternative when JAX routes do not serve a specific destination.", url: "https://www.flydaytonafirst.com/", schemaType: "Airport" },
];

const photoLocations = [
  { name: "CVS Pharmacy — Atlantic Blvd", address: "10601 Atlantic Blvd, Jacksonville, FL 32225", hours: "Daily 7 AM – 11 PM" },
  { name: "Walgreens — Riverside", address: "1321 King St, Jacksonville, FL 32204", hours: "Call for hours" },
  { name: "CVS Pharmacy — Orange Park", address: "1065 Blanding Blvd, Orange Park, FL 32073", hours: "Daily 7 AM – 11 PM" },
  { name: "The UPS Store — San Marco", address: "1974 San Marco Blvd, Jacksonville, FL 32207", hours: "Call for hours" },
  { name: "Walgreens — St. Augustine", address: "1800 US Highway 1 S, St. Augustine, FL 32084", hours: "Call for hours" },
  { name: "CVS Pharmacy — Fernandina Beach", address: "1900 S 14th St, Fernandina Beach, FL 32034", hours: "Daily 7 AM – 11 PM" },
  { name: "FedEx Office — Downtown Jacksonville", address: "11 E Forsyth St, Jacksonville, FL 32202", hours: "Call for hours" },
  { name: "Walgreens — Fleming Island", address: "1500 County Rd 220, Fleming Island, FL 32003", hours: "Call for hours" },
];

const dmvLocations = [
  {
    name: "Duval County Tax Collector — Main Office",
    address: "231 E Forsyth St, Jacksonville, FL 32202",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "Driver licenses, REAL ID, titles & registration. Appointment recommended.",
    phone: "(904) 255-5700",
    url: "https://www.dctcmv.com/",
    featured: true,
  },
  {
    name: "Duval County Tax Collector — Southside",
    address: "9832 Old Baymeadows Rd, Jacksonville, FL 32256",
    hours: "Mon–Fri, 8:00 AM – 5:00 PM",
    note: "Southside Jacksonville option. Call ahead for current wait times.",
    phone: "(904) 255-5700",
    url: "https://www.dctcmv.com/",
    featured: false,
  },
];

const vitalRecordsLinks = [
  {
    icon: "ri-user-smile-line",
    label: "Birth Certificate",
    desc: "Request certified birth certificates from the Florida Department of Health in Duval County. Online, mail, or in-person options available.",
    url: "https://duval.floridahealth.gov/",
  },
  {
    icon: "ri-file-list-2-line",
    label: "Death Certificate",
    desc: "Obtain certified death certificates through the Florida Department of Health in Duval County. Required for estate and legal matters.",
    url: "https://duval.floridahealth.gov/",
  },
  {
    icon: "ri-hearts-line",
    label: "Marriage Certificate",
    desc: "Request marriage license records from the Duval County Clerk of Courts. Certified copies available for name changes and legal purposes.",
    url: "https://www.duvalclerk.com/",
  },
  {
    icon: "ri-file-copy-line",
    label: "Divorce Decree",
    desc: "Obtain certified divorce records through the Duval County Clerk of Courts. Required for name change passport corrections and legal documentation.",
    url: "https://www.duvalclerk.com/",
  },
];

export default function JacksonvilleHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 6);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Helpful Resources for Jacksonville Travelers
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Northeast Florida resources to help you gather everything you need before your next international trip.
          </p>
        </div>

        <div className="space-y-6">

          {/* Passport Photos */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near Jacksonville</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-2">
                Your passport application requires two identical 2&quot;×2&quot; passport photos taken within the last 6 months. Popular passport photo locations near Jacksonville include:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button
                  onClick={() => setPhotoExpanded(true)}
                  className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors"
                >
                  <i className="ri-add-circle-line text-sm"></i>
                  Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://travel.state.gov/content/travel/en/passports/how-apply/photos.html" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>U.S. State Dept. Official Photo Requirements
                </a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i>
                  <strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications — ask the technician to confirm compliance before you leave.
                </p>
              </div>
            </div>
          </div>

          {/* Vital Records */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-file-text-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Vital Records</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Birth, Death, Marriage &amp; Divorce Records — Duval County</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                If you need a certified copy of your birth certificate for a passport application, a marriage certificate for a name change, or a divorce decree to support your passport correction, here are the official sources for vital records in Duval County. Most records can be ordered online for delivery within 5–10 business days.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                {vitalRecordsLinks.map((record, i) => (
                  <a
                    key={i}
                    href={record.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-3.5 bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 mt-0.5">
                      <i className={`${record.icon} text-[#1A365D] text-base group-hover:text-[#F6C344] transition-colors`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{record.label}</p>
                      <p className="text-gray-500 text-xs leading-relaxed mb-1.5">{record.desc}</p>
                      <span className="inline-flex items-center gap-1 text-[#1A365D] font-semibold text-xs group-hover:text-[#F6C344] transition-colors">
                        <i className="ri-external-link-line text-xs"></i>Get Records
                      </span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-information-line text-amber-500 mr-1.5"></i>
                  <strong>Don&apos;t see your county?</strong> If the record you need was issued outside Duval County, visit <a href="https://www.vitalchek.com/" target="_blank" rel="noopener noreferrer" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">VitalChek.com</a> — the official online service for ordering vital records nationwide across all U.S. states and counties.
                </p>
              </div>
            </div>
          </div>

          {/* DMV */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Duval County Tax Collector — DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                A valid government-issued photo ID is required for your passport application. If you need a new or renewed Florida driver&apos;s license or REAL ID, the Duval County Tax Collector handles DMV services in the Jacksonville area.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-[#1A365D] text-base"></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                        <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <a href={`tel:${loc.phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-1 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                            <i className="ri-phone-line text-xs"></i>{loc.phone}
                          </a>
                          <a href={loc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                            <i className="ri-external-link-line text-xs"></i>Website
                          </a>
                        </div>
                        <p className="text-[#1A365D] text-xs font-medium mt-1.5 leading-snug">{loc.note}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4 mb-4">
                <a href="https://www.dctcmv.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>Duval County Tax Collector
                </a>
                <a href="https://www.flhsmv.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                  <i className="ri-external-link-line text-sm"></i>FLHSMV — Florida DMV
                </a>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-calendar-check-line text-amber-500 mr-1.5"></i>
                  <strong>Appointment recommended</strong> — walk-in wait times can be long. Book your slot online at the Duval County Tax Collector website before visiting.
                </p>
              </div>
            </div>
          </div>

          {/* Airports */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving Jacksonville Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Jacksonville International Airport (JAX) is the primary commercial airport for Northeast Florida. If your trip is coming up fast, make sure your passport is in hand well before your departure date.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {[
                  {
                    code: "JAX",
                    name: "Jacksonville International Airport",
                    distance: "Northside Jacksonville — about 15 miles from downtown",
                    address: "2400 Yankee Clipper Dr, Jacksonville, FL 32218",
                    note: "Primary commercial airport for Northeast Florida. Nonstop service to major domestic and international hubs including Atlanta, Charlotte, Miami, New York, and Toronto.",
                    flightUrl: "https://www.flyjacksonville.com/",
                    flightLabel: "JAX Airport Website",
                    featured: true,
                  },
                  {
                    code: "DAB",
                    name: "Daytona Beach International Airport",
                    distance: "Daytona Beach — about 90 miles south of Jacksonville",
                    address: "700 Catalina Dr, Daytona Beach, FL 32114",
                    note: "Secondary option for Northeast Florida travelers, especially for budget carriers and seasonal routes.",
                    flightUrl: "https://www.flydaytonafirst.com/",
                    flightLabel: "Daytona Beach Airport",
                    featured: false,
                  },
                ].map((airport, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${airport.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[#1A365D] font-black text-xl leading-none">{airport.code}</span>
                      {airport.featured && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                      )}
                    </div>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{airport.name}</p>
                    <div className="flex items-center gap-1.5 text-xs text-[#F6C344] font-semibold mb-2">
                      <i className="ri-road-map-line"></i>
                      <span>{airport.distance}</span>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed mb-1">{airport.address}</p>
                    <p className="text-gray-400 text-xs leading-relaxed mb-3">{airport.note}</p>
                    <a href={airport.flightUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors">
                      <i className="ri-external-link-line text-sm"></i>{airport.flightLabel}
                    </a>
                  </div>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.
                </p>
              </div>
            </div>
          </div>

          {/* Nearby Landmarks */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-landscape-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Nearby Landmarks</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Key Landmarks &amp; Transportation Hubs Near Jacksonville</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">
                Jacksonville is a major military hub and the largest city by area in the contiguous United States. These landmarks and transportation hubs — from two major Navy installations to the JAXPORT cruise terminal — define travel patterns for Northeast Florida's military families, business travelers, and cruise passengers.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
                {landmarks.map((landmark, i) => (
                  <a
                    key={i}
                    href={landmark.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-4 hover:border-[#F6C344] transition-all group"
                  >
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0 group-hover:bg-[#F6C344]/15 transition-colors">
                        <i className={`${landmark.icon} text-[#1A365D] text-sm`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#1A365D] text-xs leading-snug">{landmark.name}</p>
                        <p className="text-gray-400 text-[11px]">{landmark.distance}</p>
                      </div>
                    </div>
                    <p className="text-gray-500 text-xs leading-relaxed">{landmark.desc}</p>
                  </a>
                ))}
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed">
                  <i className="ri-passport-line text-amber-500 mr-1.5"></i>
                  <strong>Passport required for all international travel.</strong> Whether you are deploying from NAS Jacksonville or Mayport, boarding a cruise from JAXPORT, or flying out of JAX on an international itinerary — a valid U.S. passport is mandatory. <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">Expedited processing through Fast Passport Center</a> is the fastest option for Jacksonville residents.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}